import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatListItem } from "@/components/chat/ChatListItem"
import { IosNotificationSettingsDialog } from '@/components/shared/IosNotificationSettingsDialog'
import { useArtists } from "@/hooks/useArtists"
import { useUsers } from "@/hooks/useUsers"
import { useAuthState } from "@/hooks/useAuth"
import { usePlatformDetection } from "@/hooks/usePlatformDetection"
import { useBookingsForChats } from "@/hooks/useBookingsForChats"
import {
  subscribeIosEnabled,
  subscribeAndroidEnabled,
  setIosEnabled,
  setAndroidEnabled,
  syncIosFcmTokenFromPreferences,
  syncAndroidFcmTokenFromPreferences,
} from "@/lib/notifications"
import { PushNotifications } from '@capacitor/push-notifications'
import { Bell } from 'lucide-react'
import { ChatsIcon } from "@/lib/navigationItems"
import type { Chat } from "@/types/models"
import { 
  loadChatListCache, 
  saveChatListCache, 
  computeChatDisplayData,
  type CachedChatDisplayData 
} from "@/lib/chatListCache"
import { getUnreadCount, getArtistUnreadCount } from "@/lib/chatData"
import { auth } from "@/lib/firebase"

export type SortOption = 'booking_priority' | 'recent'

interface ChatListProps {
  isArtistView?: boolean
  className?: string
  searchQuery?: string
  sortBy?: SortOption
}

export function ChatList({ 
  isArtistView = false, 
  className = "", 
  searchQuery = "",
  sortBy = 'recent'
}: ChatListProps) {
  const { t } = useTranslation()
  const { user } = useAuthState()
  const { showNativePushToggle, isIOSNative, isIOSWeb, isAndroidNative } = usePlatformDetection()
  const [chats, setChats] = useState<Chat[]>([])
  
  // Load cache synchronously on initialization to prevent flicker
  // Use auth.currentUser to get user ID immediately from Firebase (persisted auth)
  const [cachedDisplayData, setCachedDisplayData] = useState<CachedChatDisplayData[]>(() => {
    const currentUserId = auth.currentUser?.uid
    if (!currentUserId) return []
    
    const cached = loadChatListCache(currentUserId, isArtistView)
    if (cached && cached.length > 0) {
      console.log('[ChatList] ⚡ Initial cached data loaded synchronously:', cached.length, 'items')
      return cached
    }
    return []
  })
  
  
  const [isShowingCachedData, setIsShowingCachedData] = useState(() => {
    const currentUserId = auth.currentUser?.uid
    if (!currentUserId) return false
    
    const cached = loadChatListCache(currentUserId, isArtistView)
    const hasCache = !!(cached && cached.length > 0)
    
    if (hasCache) {
      console.log('[ChatList] ⚡ Showing cached data instantly (no flicker!)')
    }
    
    return hasCache
  })
  
  const [fcmEnabled, setFcmEnabled] = useState<boolean>(false)
  const [updating, setUpdating] = useState<boolean>(false)
  const [showIosNotificationDeniedDialog, setShowIosNotificationDeniedDialog] = useState(false)
  
  // Track if we've received the initial data load (to prevent showing empty state prematurely)
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(() => {
    // If we loaded cache, consider initial data loaded
    const currentUserId = auth.currentUser?.uid
    if (!currentUserId) return false
    const cached = loadChatListCache(currentUserId, isArtistView)
    return !!(cached && cached.length > 0)
  })
  
  // Get unique artist IDs from chats (for regular users) or user IDs (for artists)
  const artistIds = chats.map(chat => chat.artistId)
  const userIds = chats.map(chat => chat.userId)
  const { artists } = useArtists(artistIds)
  const { users } = useUsers(userIds)

  // Fetch booking statuses for these chats
  const chatIds = chats.map(c => c.id)
  const { bookingStatuses, refetch: refetchBookingStatuses } = useBookingsForChats(chatIds)

  // Update cache whenever we have fresh chat data
  useEffect(() => {
    if (!user?.uid || chats.length === 0 || isShowingCachedData) return
    
    // Only update cache when we have all the supporting data
    if (artists.length === 0 && users.length === 0) return
    
    console.log('[ChatList] Computing and caching display data for', chats.length, 'chats')
    
    const displayDataList: CachedChatDisplayData[] = chats.map(chat => {
      // Get supporting data for this chat
      const artist = artists.find(a => a.id === chat.artistId) || null
      const chatUser = users.find(u => u.uid === chat.userId) || null
      
      // Compute unread count and message preview
      const unreadCount = isArtistView 
        ? getArtistUnreadCount(chat as any, user.uid)
        : getUnreadCount(chat as any, user.uid)
      
      // Get booking info
      const bookingId = chat.bookingId || null
      const bookingStatus = bookingId ? bookingStatuses[chat.id] : undefined
      
      return computeChatDisplayData(
        chat,
        artist,
        chatUser,
        isArtistView,
        unreadCount,
        bookingId,
        bookingStatus,
        user.uid,
      )
    })
    
    // Save to cache
    saveChatListCache(user.uid, isArtistView, displayDataList)
    
    // Update local state to use computed data
    setCachedDisplayData(displayDataList)
  }, [chats, artists, users, isArtistView, user?.uid, isShowingCachedData, bookingStatuses])

  // Update cache when user or view mode changes (not needed on initial mount anymore)
  useEffect(() => {
    if (!user?.uid) {
      // Clear cached data if user is logged out
      setCachedDisplayData([])
      setIsShowingCachedData(false)
      setHasInitialDataLoaded(false)
      return
    }
    
    // Reload cache when user or view mode changes
    const cached = loadChatListCache(user.uid, isArtistView)
    
    if (cached && cached.length > 0) {
      console.log('[ChatList] Cache updated for view change:', cached.length, 'items')
      setCachedDisplayData(cached)
      setIsShowingCachedData(true)
      setHasInitialDataLoaded(true) // If we have cache, consider initial data loaded
    } else {
      console.log('[ChatList] No cache found after view change')
      setCachedDisplayData([])
      setIsShowingCachedData(false)
      setHasInitialDataLoaded(false) // Wait for fresh data
    }
  }, [user?.uid, isArtistView])

  useEffect(() => {
    if (!user?.uid || (!isIOSNative && !isIOSWeb)) return
    const unsub = subscribeIosEnabled(user.uid, setFcmEnabled)
    return () => unsub()
  }, [user?.uid, isIOSNative, isIOSWeb])

  useEffect(() => {
    if (!user?.uid || !isAndroidNative) return
    const unsub = subscribeAndroidEnabled(user.uid, setFcmEnabled)
    return () => unsub()
  }, [user?.uid, isAndroidNative])

  // Subscribe to chats in real-time
  useEffect(() => {
    if (!user) {
      setChats([])
      setIsShowingCachedData(false)
      setHasInitialDataLoaded(false) // Reset when user logs out
      return
    }

    const setupChatSubscription = async () => {
      try {
        console.log('[ChatList] Setting up chat subscription, isArtistView:', isArtistView)
        
        // Import subscription functions dynamically
        const { subscribeToUserChats, subscribeToArtistChats } = await import('@/lib/chatData')
        
        let unsubscribe: (() => void) | undefined
        
        if (isArtistView) {
          // For artists, we need to await the subscription setup
          unsubscribe = await subscribeToArtistChats((updatedChats) => {
            console.log('[ChatList] Artist chats updated, count:', updatedChats.length)
            setChats(updatedChats)
            setHasInitialDataLoaded(true) // Mark that we've received initial data
            // Don't immediately switch away from cache - let the display data computation decide
            // when to stop showing cached data based on whether we have valid fresh data
          })
        } else {
          // For regular users
          unsubscribe = subscribeToUserChats((updatedChats) => {
            console.log('[ChatList] User chats updated, count:', updatedChats.length)
            setChats(updatedChats)
            setHasInitialDataLoaded(true) // Mark that we've received initial data
            // Don't immediately switch away from cache - let the display data computation decide
            // when to stop showing cached data based on whether we have valid fresh data
          })
        }
        
        // Return cleanup function
        return unsubscribe
      } catch (error) {
        console.error('[ChatList] Error setting up chat subscription:', error)
        setIsShowingCachedData(false)
        return undefined
      }
    }

    const cleanupPromise = setupChatSubscription()

    return () => {
      // Clean up subscription when component unmounts
      cleanupPromise.then(cleanup => {
        if (cleanup) {
          console.log('[ChatList] Cleaning up chat subscription')
          cleanup()
        }
      })
    }
  }, [user, isArtistView])

  // Sort and filter display data - show cached instantly, update with fresh data when ready
  const sortedAndFilteredDisplayData = useMemo(() => {
    // Check if we have fresh data ready to use (all supporting data loaded)
    const hasFreshData = chats.length > 0
    const hasSupportingData = (isArtistView && users.length > 0) || (!isArtistView && artists.length > 0) || chats.length === 0
    const useFreshData = hasFreshData && hasSupportingData
    
    // Auto-transition away from cached data state when we're using fresh data
    if (isShowingCachedData && useFreshData) {
      console.log('[ChatList] ⚡ Transitioning from cached to fresh data')
      setIsShowingCachedData(false)
    }
    
    // Determine which data to display: fresh if available, otherwise cache
    let dataToDisplay: CachedChatDisplayData[]
    
    if (useFreshData) {
      // We have fresh data - compute it
      console.log('[ChatList] Using fresh data, chats:', chats.length, 'artists:', artists.length, 'users:', users.length)
      
      let filtered = chats
      
      // Apply search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase()
        filtered = filtered.filter(chat => {
          if (isArtistView) {
            // For artists, search by user info
            const user = users.find(u => u.uid === chat.userId)
            return (
              user?.instagram?.toLowerCase().includes(searchLower) ||
              user?.displayName?.toLowerCase().includes(searchLower) ||
              user?.email?.toLowerCase().includes(searchLower)
            )
          } else {
            // For users, search by artist info
            const artist = artists.find(a => a.id === chat.artistId)
            return (
              artist?.firstName?.toLowerCase().includes(searchLower) ||
              artist?.lastName?.toLowerCase().includes(searchLower)
            )
          }
        })
      }
      
      // Apply sorting
      const sorted = [...filtered].sort((a, b) => {
        if (sortBy === 'booking_priority') {
          // Booking Priority: chats with bookings come first
          const aHasBooking = !!a.bookingId
          const bHasBooking = !!b.bookingId
          
          if (aHasBooking && !bHasBooking) {
            return -1
          } else if (!aHasBooking && bHasBooking) {
            return 1
          } else {
            // If both have bookings or both don't, sort by recency
            return b.lastMessageAt.getTime() - a.lastMessageAt.getTime()
          }
        } else {
          // Recent: sort by last message time
          return b.lastMessageAt.getTime() - a.lastMessageAt.getTime()
        }
      })
      
      // Map to display data for consistent rendering
      dataToDisplay = sorted.map(chat => {
        // Find in cached display data if available
        const cached = cachedDisplayData.find(c => c.chatId === chat.id)
        if (cached) return cached
        
        // Otherwise compute on the fly
        const artist = artists.find(a => a.id === chat.artistId) || null
        const chatUser = users.find(u => u.uid === chat.userId) || null
        const unreadCount = isArtistView 
          ? getArtistUnreadCount(chat as any, user?.uid || '')
          : getUnreadCount(chat as any, user?.uid || '')
        const bookingId = chat.bookingId || null
        const bookingStatus = bookingId ? bookingStatuses[chat.id] : undefined
        
        return computeChatDisplayData(
          chat,
          artist,
          chatUser,
          isArtistView,
          unreadCount,
          bookingId,
          bookingStatus,
          user?.uid || '',
        )
      })
    } else {
      // Fresh data not ready yet - use cache (instant display!)
      console.log('[ChatList] ⚡ Using cached data instantly, length:', cachedDisplayData.length)
      dataToDisplay = cachedDisplayData
    }
    
    // Apply search filter to whatever data we're showing
    // This must work with BOTH cached and fresh data
    let filtered = dataToDisplay
    
    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = filtered.filter(item => 
        item.displayName.toLowerCase().includes(searchLower) ||
        item.displaySecondary.toLowerCase().includes(searchLower)
      )
    }
    
    // Apply sorting to final data
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'booking_priority') {
        // Booking Priority: chats with bookings come first
        if (a.hasBooking && !b.hasBooking) {
          return -1
        } else if (!a.hasBooking && b.hasBooking) {
          return 1
        } else {
          // If both have bookings or both don't, sort by recency
          return b.lastMessageAt - a.lastMessageAt
        }
      } else {
        // Recent: sort by last message time
        return b.lastMessageAt - a.lastMessageAt
      }
    })
    
    console.log('[ChatList] Displaying', sorted.length, 'items (using', useFreshData ? 'fresh' : 'cached', 'data)')
    return sorted
  }, [cachedDisplayData, chats, artists, users, isArtistView, user?.uid, sortBy, searchQuery, bookingStatuses])

  const handleToggleNotifications = async () => {
    if (!user?.uid || !(isIOSNative || isAndroidNative)) return
    const next = !fcmEnabled
    try {
      setUpdating(true)
      if (next) {
        try {
          const { receive } = await PushNotifications.checkPermissions()
          if (receive === 'denied') {
            setShowIosNotificationDeniedDialog(true)
            return
          }
        } catch {
          /* fall through */
        }
        if (isIOSNative) {
          const syncSucceeded = await syncIosFcmTokenFromPreferences(user.uid)
          if (!syncSucceeded) setShowIosNotificationDeniedDialog(true)
        } else {
          const syncSucceeded = await syncAndroidFcmTokenFromPreferences(user.uid)
          if (!syncSucceeded) setShowIosNotificationDeniedDialog(true)
        }
      } else {
        if (isIOSNative) await setIosEnabled(user.uid, false)
        else await setAndroidEnabled(user.uid, false)
      }
    } finally {
      setUpdating(false)
    }
  }

  const notificationsEnabled = fcmEnabled

  return (
    <div className={className}>
      <IosNotificationSettingsDialog
        isOpen={showIosNotificationDeniedDialog}
        onOpenChange={setShowIosNotificationDeniedDialog}
      />
      {/* Chat List */}
      <div className="bg-background pb-4">
        {!user ? (
          // Show login prompt when user is not authenticated
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t('chats.loginRequired')}
            </h3>
            <p className="text-muted-foreground max-w-sm">
              {t('chats.loginRequiredDescription')}
            </p>
          </div>
        ) : sortedAndFilteredDisplayData.length > 0 ? (
          // Show chat cards (either from cache or fresh data) - NO INTERMEDIATE STATES!
          <div className="flex flex-col gap-1">
            {sortedAndFilteredDisplayData.map((displayData) => (
              <ChatListItem
                key={displayData.chatId}
                displayData={displayData}
                isArtistView={isArtistView}
                onBookingComplete={refetchBookingStatuses}
                compact
              />
            ))}
          </div>
        ) : hasInitialDataLoaded ? (
          // Show empty state ONLY when we know for sure there's no data (not while loading)
          <div className="flex flex-col items-center justify-center py-12 text-center px-6">
            {/* Icon */}
            <div className="mb-6">
              <ChatsIcon className="w-12 h-12 text-white mx-auto" />
            </div>
            
            {/* Message */}
            <h3 className="text-lg font-medium text-foreground mb-1">
              {searchQuery ? t('chats.noSearchResults') : t('chats.noChatsTitle')}
            </h3>
            <p className="text-muted-foreground max-w-sm mb-6">
              Messages from clients will appear here!
            </p>

            {/* Notification Settings - Show if user is logged in and platform supports notifications */}
            {user && showNativePushToggle && (
              <button
                type="button"
                onClick={handleToggleNotifications}
                disabled={updating}
                className="w-full max-w-xs flex items-center justify-between p-4 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                  <div className="text-left">
                    <p className="font-medium text-sm">{t('settings.notifications', 'Notifications')}</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationsEnabled ? t('common.enabled', 'Enabled') : t('common.disabled', 'Disabled')}
                    </p>
                  </div>
                </div>
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${notificationsEnabled ? 'bg-primary' : 'bg-card'}`}>
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full border shadow-sm transition-transform ${
                      notificationsEnabled
                        ? 'translate-x-5 border-white/30 bg-primary-foreground'
                        : 'translate-x-1 border-border bg-muted dark:bg-zinc-200'
                    }`}
                  />
                </div>
              </button>
            )}
          </div>
        ) : (
          // Loading state: Show nothing while waiting for initial data - prevents flicker!
          // If cache exists, cached data should already be rendering above
          null
        )}
      </div>
    </div>
  )
} 